import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { changelog, getChangelogById, ChangelogEntry } from '../data/changelog';
import './Changelog.css';

const ChangeTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'feature':
      return <span className="change-icon feature" title="New Feature">+</span>;
    case 'fix':
      return <span className="change-icon fix" title="Bug Fix">*</span>;
    case 'improvement':
      return <span className="change-icon improvement" title="Improvement">^</span>;
    case 'design':
      return <span className="change-icon design" title="Design Update">~</span>;
    default:
      return <span className="change-icon">-</span>;
  }
};

const ChangelogEntryComponent: React.FC<{ entry: ChangelogEntry; isExpanded?: boolean }> = ({ entry, isExpanded = false }) => {
  const navigate = useNavigate();

  return (
    <div className={`changelog-entry ${isExpanded ? 'expanded' : ''}`}>
      <div className="changelog-entry-header">
        <div className="changelog-date">{entry.date}</div>
        <h2
          className="changelog-title"
          onClick={() => !isExpanded && navigate(`/changelog/${entry.id}`)}
          style={{ cursor: isExpanded ? 'default' : 'pointer' }}
        >
          {entry.title}
        </h2>
        {entry.games && entry.games.length > 0 && (
          <div className="changelog-games">
            {entry.games.map(game => (
              <span key={game} className="game-tag">{game}</span>
            ))}
          </div>
        )}
      </div>

      <p className="changelog-summary">{entry.summary}</p>

      <ul className="changelog-changes">
        {entry.changes.map((change, index) => (
          <li key={index} className={`change-item ${change.type}`}>
            <ChangeTypeIcon type={change.type} />
            <span>{change.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Single changelog entry page (for direct linking)
export const ChangelogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const entry = id ? getChangelogById(id) : undefined;

  if (!entry) {
    return (
      <div className="changelog-container">
        <div className="changelog-content">
          <h1>Changelog Entry Not Found</h1>
          <p>The changelog entry you're looking for doesn't exist.</p>
          <Link to="/changelog" className="back-button">
            View All Updates
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="changelog-container">
      <div className="changelog-content">
        <Link to="/changelog" className="changelog-back-link">
          &larr; All Updates
        </Link>

        <ChangelogEntryComponent entry={entry} isExpanded={true} />

        <div className="changelog-share">
          <h3>Share this update</h3>
          <button
            className="share-button twitter"
            onClick={() => {
              const url = `${window.location.origin}/changelog/${entry.id}`;
              const text = `${entry.title}\n\n${entry.summary}\n\nCheck out what's new on mfers arcade!`;
              window.open(
                `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
                '_blank'
              );
            }}
          >
            Share on X
          </button>
          <button
            className="share-button copy"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/changelog/${entry.id}`);
              alert('Link copied to clipboard!');
            }}
          >
            Copy Link
          </button>
        </div>

        <Link to="/" className="changelog-back-link">
          ← Back to Games
        </Link>
      </div>
    </div>
  );
};

// Full changelog list page
const Changelog: React.FC = () => {
  return (
    <div className="changelog-container">
      <div className="changelog-content">
        <img src="/icons/logo-128x128.png" className="changelog-logo" alt="mfers arcade logo" />
        <h1>What's New</h1>
        <p className="changelog-intro">
          Weekly updates and improvements to mfers arcade
        </p>

        {/* Table of Contents */}
        <nav className="changelog-toc">
          <h2>Updates</h2>
          <ul>
            {changelog.map(entry => (
              <li key={entry.id}>
                <Link to={`/changelog/${entry.id}`}>
                  <span className="toc-date">{entry.date}</span>
                  <span className="toc-title">{entry.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="changelog-list">
          {changelog.map(entry => (
            <ChangelogEntryComponent key={entry.id} entry={entry} />
          ))}
        </div>

        <Link to="/" className="changelog-back-link">
          ← Back to Games
        </Link>
      </div>
    </div>
  );
};

export default Changelog;
